// ════════════════════════════════════════════════════════════════════════
//  Welche veröffentlichten Schlüssel sind belegt?
//
//  Entfernen ist gesperrt, solange Fortschritt daran hängt – nicht, weil einmal
//  veröffentlicht wurde. Die Regel selbst steht als Trigger in der Datenbank
//  (`curriculum_version_publish_check`); dieser Hook ist nur dafür da, dass die
//  Oberfläche keinen Knopf anbietet, den der Trigger hinterher ablehnt. Dasselbe
//  Muster wie `useCourseHasResults` in der Verwaltung.
//
//  Die Admin-App sieht dabei keine Schülerdaten: `stage_keys_in_use` gibt
//  ausschließlich eine Teilmenge dessen zurück, was sie hineinreicht.
//
//  Kurze Standzeit. Ein Kind, das während der Redaktionsarbeit die erste Stufe
//  übt, macht aus einem entfernbaren Schlüssel einen belegten – wer den Knopf
//  dann doch drückt, läuft in den Trigger, und das ist der richtige Ausgang,
//  aber der seltenere soll es bleiben.
// ════════════════════════════════════════════════════════════════════════

import { useQuery } from "@tanstack/react-query";
import type { OttoClient } from "@otto/plattform";

const EMPTY: ReadonlySet<string> = new Set();

export function useStageKeysInUse(client: OttoClient, keys: ReadonlySet<string>) {
  // Sortiert, damit dieselbe Menge denselben Schlüssel ergibt: die Reihenfolge
  // aus `stageKeysOf` hängt am Bundle und ist keine Aussage.
  const asked = [...keys].sort();

  return useQuery({
    queryKey: ["stage-keys-in-use", asked],
    staleTime: 60 * 1000,
    // Ohne Schlüssel gibt es nichts zu fragen – ein Lehrgang ohne
    // veröffentlichte Fassung hat keine festen Schlüssel.
    enabled: asked.length > 0,
    queryFn: async (): Promise<ReadonlySet<string>> => {
      const { data, error } = await client.rpc("stage_keys_in_use", { p_keys: asked });
      if (error) throw error;
      // Keine Antwort heißt hier „nichts belegt“, und damit stünde das Entfernen
      // offen. Das ist vertretbar, weil die Sperre nicht hier liegt: der Trigger
      // lehnt ab, was er ablehnen muss. Andersherum – im Zweifel alles sperren –
      // wäre die Oberfläche bei jedem Netzfehler grundlos zu.
      return Array.isArray(data) ? new Set(data) : EMPTY;
    },
  });
}
